// src/hooks/useTaskService.js
import { useState, useEffect, useCallback } from 'react';
import { AssessmentService } from '../services/taskService';

export const useAssessmentService = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Generic service call wrapper
  const callService = useCallback(async (serviceMethod, ...args) => {
    setLoading(true);
    setError(null);

    try {
      const result = await serviceMethod(...args);
      return result;
    } catch (err) {
      setError(err.message || 'An error occurred');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    clearError,
    // Task methods
    createTask: useCallback((taskData) => 
      callService(AssessmentService.createAssessmentTask, taskData), [callService]),
    getTasks: useCallback(() => 
      callService(AssessmentService.getAssessmentTasks), [callService]),
    getTask: useCallback((taskId) => 
      callService(AssessmentService.getAssessmentTask, taskId), [callService]),
    updateTask: useCallback((taskId, updates) => 
      callService(AssessmentService.updateAssessmentTask, taskId, updates), [callService]),
    deleteTask: useCallback((taskId) => 
      callService(AssessmentService.deleteAssessmentTask, taskId), [callService]),
    shareTask: useCallback((taskId, userIds) => 
      callService(AssessmentService.shareTask, taskId, userIds), [callService]),
    unshareTask: useCallback((taskId, userIds) => 
      callService(AssessmentService.unshareTask, taskId, userIds), [callService]),
    // File methods
    uploadFile: useCallback((file, taskId, fileName) => 
      callService(AssessmentService.uploadMedia, file, taskId, fileName), [callService]),
    deleteFile: useCallback((taskId, fileName) => 
      callService(AssessmentService.deleteMedia, taskId, fileName), [callService]),
    // Submission methods
    createSubmission: useCallback((submissionData) => 
      callService(AssessmentService.createTaskSubmission, submissionData), [callService]),
    getSubmissions: useCallback((taskId) => 
      callService(AssessmentService.getTaskSubmissions, taskId), [callService]),
    // Marks methods
    createMarks: useCallback((marksData) => 
      callService(AssessmentService.createLearnerMark, marksData), [callService]),
    getMarks: useCallback((taskId, submissionId) => 
      callService(AssessmentService.getLearnerMarks, taskId, submissionId), [callService]),
    bulkCreateMarks: useCallback((marksArray) => 
      callService(AssessmentService.bulkCreateLearnerMarks, marksArray), [callService]),
    duplicateTask: useCallback((taskId) => 
      callService(AssessmentService.duplicateAssessmentTask, taskId), [callService]),
  };
};